import { useEffect } from 'react';
import { useAuthModal } from '../../contexts/AuthModalContext';
import AuthFlow from './AuthFlow';
import { SESSION_ENDED_MESSAGE } from '../../utils/sessionRetry';

// Romaya's own modal shell -- same visual result as the funnel's
// AuthModalShell + AuthSurfaceShell (dimmed/blurred backdrop, centered card
// on desktop, bottom sheet on mobile), without Radix or Next's routing.
// Outside-click-to-close is off on purpose, matching the funnel's own modal.
export default function AuthModal() {
  const { isOpen, isAuthenticating, sessionEnded, close } = useAuthModal();

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="RoamMyWay authentication"
      className="fixed inset-0 z-[9999] flex min-h-[100dvh] items-end justify-center overflow-y-auto outline-none lg:items-center lg:px-4 lg:pb-4 lg:pt-4"
    >
      <div
        className="fixed inset-0 bg-[#14354C]/60 lg:backdrop-blur-[7px]"
        aria-hidden="true"
      />
      <div className="relative w-full lg:mx-auto lg:my-auto lg:w-auto">
        <AuthCard>
          {sessionEnded && !isAuthenticating && (
            <p role="status" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 font-brand text-sm text-amber-900">
              {SESSION_ENDED_MESSAGE}
            </p>
          )}
          {isAuthenticating ? <AuthLoadingCard /> : <AuthFlow />}
        </AuthCard>
        <button
          type="button"
          onClick={close}
          aria-label="Close authentication"
          className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full text-brand-text-secondary transition-colors hover:bg-brand-grey hover:text-brand-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal-strong lg:right-4 lg:top-4"
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  );
}

// Shown in place of AuthFlow once sign-in has succeeded (e.g. the OAuth
// popup closed) but we're still re-verifying the real session -- so the
// form can't stay editable/clickable during that gap.
function AuthLoadingCard() {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-brand-blue">
      <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-brand-teal-strong" />
      <span className="font-brand text-sm font-medium">Signing you in...</span>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

// Single fixed-width card (no side-by-side promo panel like the funnel's
// shell can have), sized to match the funnel's own reference exactly.
function AuthCard({ children }) {
  return (
    <div className="animate-auth-surface w-full rounded-t-[24px] bg-white px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6 shadow-none lg:w-[600px] lg:rounded-[26px] lg:px-16 lg:py-14 lg:shadow-[0_26px_64px_rgba(9,28,42,0.3)]">
      <div className="mb-3 flex justify-center lg:hidden" aria-hidden="true">
        <div className="h-1 w-10 rounded-full bg-brand-border" />
      </div>

      <div className="mb-6 flex items-center justify-start lg:mb-8 lg:justify-center">
        <img src="/RoamMyWay.png" alt="RoamMyWay" className="h-9 w-auto object-contain lg:h-12" />
      </div>

      <section>{children}</section>

      <div className="mt-4 text-center">
        <p className="font-brand text-[11.5px] leading-[1.55] text-brand-text-secondary">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
